import type { $Fetch } from "nitropack";

// Types for admin API responses - matching Rust structs
export interface AdminUserDetails {
    user_id: string;
    first_name: string;
    last_name: string;
    email: string;
    is_admin: boolean;
    is_validated: boolean;
    is_mentor: boolean;
    is_organization: boolean;
    created_at: string;
    freelance_handle: string | null;
    is_visible: boolean | null;
    company_name: string | null;
    ingoing_missions_count: number;
    ingoing_invoices_count: number;
    ingoing_transfers_count: number;
    // When set, the user is deactivated and excluded from all outbound
    // communications (crons + newsletter). null = active.
    deactivated_at: string | null;
}

export interface ListUsersResponse {
    users: AdminUserDetails[];
    total: number;
}

export interface AdminDocument {
    file_id: string;
    document_name: string;
    company_id: string;
    company_name: string;
    validation_status: string;
    rejection_reason: string | null;
    expires_at: string | null;
    created_at: string;
}

export interface ListDocumentsResponse {
    documents: AdminDocument[];
    total: number;
}

export type AdminMissionSort =
    | 'created_at_desc' | 'created_at_asc'
    | 'candidates_desc' | 'candidates_asc';

export interface AdminMissionListItem {
    id: string;
    title: string;
    company_id: string;
    company_name: string;
    status: string;
    created_at: string;
    candidates_count: number;
}

export interface ListAdminMissionsResponse {
    missions: AdminMissionListItem[];
    total: number;
    page: number;
    page_size: number;
}

export interface ImpersonateResponse {
    redirect_url: string;
}

export interface AdminInvoiceListItem {
    invoice_id: string;
    mission_id: string;
    number: number;
    status: string; // draft | open | funds_partially_received | funds_received | paid | void
    amount_ttc: number; // centimes
    created_at: string;
    freelance_name: string | null;
    client_name: string | null;
}

export interface ListAdminInvoicesResponse {
    invoices: AdminInvoiceListItem[];
    total: number;
}

// --- Finance KPIs (ticket #177) ---
export interface FinanceCaTiers {
    encaisse_cents: number;
    emis_cents: number;
    potentiel_cents: number;
}

export interface FinanceFunnelStage {
    status: 'draft' | 'sent' | 'collected';
    n: number;
    sum_cents: number;
}

export interface FinanceAgingBucket {
    bucket: '0-15' | '15-30' | '30+';
    n: number;
    sum_cents: number;
}

export interface FinanceToChaseInvoice {
    invoice_id: string;
    client_name: string;
    amount_cents: number;
    days_open: number;
}

export interface FinanceMonthlyPoint {
    month: string;
    emis_cents: number;
    encaisse_cents: number;
}

export interface FinanceOverview {
    ca: FinanceCaTiers;
    funnel: FinanceFunnelStage[];
    aging: {
        threshold_days: number;
        buckets: FinanceAgingBucket[];
        to_chase: FinanceToChaseInvoice[];
    };
    monthly: FinanceMonthlyPoint[];
}

// --- Freelance KPIs (ticket #177, increment 2) ---
export interface FreelanceCompletion {
    total: number;
    created: number;
    with_siret: number;
    well_filled: number;
}

export interface FreelanceActivity {
    total: number;
    active: number;
    dormant: number;
    dormant_threshold_days: number;
}

export interface FreelanceArrival {
    month: string;
    n: number;
}

export interface FreelanceBillingItem {
    freelance_id: string;
    freelance_name: string;
    company_name: string;
    emis_cents: number;
    encaisse_cents: number;
    invoiced_missions: number;
}

export interface FreelanceBillingPage {
    items: FreelanceBillingItem[];
    total: number;
    page: number;
    page_size: number;
}

export interface FreelanceOverview {
    completion: FreelanceCompletion;
    activity: FreelanceActivity;
    arrivals: FreelanceArrival[];
}

// --- Company KPIs (ticket #177, increment 3) — company = is_organization user ---
export interface CompanyMonthlyPoint {
    month: string;
    n: number;
}

export interface CompanyRegistrations {
    total: number;
    monthly: CompanyMonthlyPoint[];
}

export interface CompanyPosting {
    posted: number;
    total: number;
    conversion_rate: number;
}

export interface CompanyMissions {
    company_id: string;
    company_name: string;
    ongoing: number;
    total: number;
}

export interface CompanyMissionsPage {
    items: CompanyMissions[];
    total: number;
    page: number;
    page_size: number;
}

export interface CompanyOverview {
    registrations: CompanyRegistrations;
    posting: CompanyPosting;
}

// --- Mission KPIs (ticket #177, increment 4) ---
export interface MissionOverview {
    total: number;
    ongoing: number;
    invoiced: number;
    invoiced_rate: number;
    avg_budget_cents: number;
    avg_duration_days: number | null;
}

export interface MissionMonthlyPoint {
    month: string;
    n: number;
}

export interface MissionDetail {
    mission_id: string;
    mission_name: string;
    company_name: string;
    budget_cents: number;
    duration_days: number | null;
    status: string;
    ca_emis_cents: number;
    ca_encaisse_cents: number;
}

export interface MissionStats {
    overview: MissionOverview;
    monthly: MissionMonthlyPoint[];
}

export interface MissionDetailPage {
    items: MissionDetail[];
    total: number;
    page: number;
    page_size: number;
}

// --- Referral validation (ticket #191) ---
// Mirrors `entities::referral::ReferralInvitation` (api/crates/core/entities/src/referral.rs).
export interface AdminReferralInvitation {
    invitation_id: string;
    referrer_user_id: string;
    invited_email: string;
    token: string;
    status: string; // pending | accepted | validated | rejected
    invited_user_id: string | null;
    created_at: string;
    validated_at: string | null;
    validated_by: string | null;
}

export interface AdminCompanyListItem {
    company_id: string;
    display_name: string;
    siren: string | null;
    contact_email: string | null;
    is_client: boolean;
    is_freelance: boolean;
}

export interface AdminCompaniesPage {
    items: AdminCompanyListItem[];
    total: number;
    page: number;
    page_size: number;
}

/** Mirror of the API `entities::company::Company`, read-only on the admin company sheet. */
export interface AdminCompanyDetail {
    company_id: string;
    business_name: string | null;
    public_name: string | null;
    display_name: string;
    phone: string | null;
    country: string;
    stripe_customer_id: string | null;
    stripe_account_id: string | null;
    // No rfc3339 serde attribute on this field: the API may send the `time` crate array format.
    stripe_onboarded_at: string | number[] | null;
    contact: { first_name: string | null; last_name: string | null; email: string | null };
    address: string | null;
    city: string | null;
    postal_code: string | null;
    billing_contact_email: string | null;
    siren: string | null;
    siret: string | null;
    ape_code: string | null;
    intra_community_vat: string | null;
    subject_vat: boolean;
    legal_form_id: string | null;
    tenant_id: string;
    created_at: string;
    updated_at: string;
}

export const AdminRepository = (client: $Fetch) => ({
    // User management
    fetchUsers: (params?: { q?: string; offset?: number; limit?: number; user_type?: string }) => {
        const queryParams = new URLSearchParams();
        if (params?.q) queryParams.append('q', params.q);
        if (params?.offset !== undefined) queryParams.append('offset', params.offset.toString());
        if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString());
        if (params?.user_type) queryParams.append('user_type', params.user_type);

        const queryString = queryParams.toString();
        const url = queryString ? `/v1/users?${queryString}` : '/v1/users';

        return client<ListUsersResponse>(url, {
            method: 'GET',
        });
    },

    fetchUserByEmail: (email: string) => {
        return client<AdminUserDetails | null>(`/v1/users/email?email=${encodeURIComponent(email)}`, {
            method: 'GET',
        });
    },

    validateUser: (userId: string, isValidated: boolean, companyId: string) => {
        const formData = new FormData();
        formData.append('is_validated', isValidated.toString());

        return client(`/v1/users/${userId}/validate?company_id=${companyId}`, {
            method: 'POST',
            body: formData,
        });
    },

    setAdminStatus: (userId: string, isAdmin: boolean, companyId: string) => {
        const formData = new FormData();
        formData.append('is_admin', isAdmin.toString());

        return client(`/v1/users/${userId}/admin?company_id=${companyId}`, {
            method: 'PUT',
            body: formData,
        });
    },

    deleteUser: (userId: string, deleteInvoices: boolean = false, deleteFiles: boolean = false) => {
        // Admin deletion - password not required, soft=false for hard delete
        // Note: deleteInvoices and deleteFiles options are not yet implemented in the backend
        return client(`/v1/users/${userId}?soft=false`, {
            method: 'DELETE',
            body: { password: null },
        });
    },

    setVisibility: (userId: string, isVisible: boolean) => {
        return client(`/v1/users/${userId}/visibility`, {
            method: 'PUT',
            body: { is_visible: isVisible },
        });
    },

    // Deactivate (true) or reactivate (false) a user for communication purposes.
    // Deactivated users are excluded from all outbound communications (crons + newsletter).
    setDeactivation: (userId: string, deactivated: boolean) => {
        return client(`/v1/users/${userId}/deactivation`, {
            method: 'PUT',
            body: { deactivated },
        });
    },

    impersonateUser: (userId: string) => {
        return client<ImpersonateResponse>(`/v1/users/${userId}/impersonate`, {
            method: 'POST',
        });
    },

    // Document management
    fetchDocuments: (params?: { status?: string; offset?: number; limit?: number }) => {
        const queryParams = new URLSearchParams();
        if (params?.status) queryParams.append('status', params.status);
        if (params?.offset !== undefined) queryParams.append('offset', params.offset.toString());
        if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString());

        const queryString = queryParams.toString();
        const url = queryString ? `/v1/files?${queryString}` : '/v1/files';

        return client<ListDocumentsResponse>(url, {
            method: 'GET',
        });
    },

    previewDocument: (fileId: string) => {
        return client(`/v1/files/${fileId}/preview`, {
            method: 'GET',
            responseType: 'blob',
        }) as Promise<Blob>;
    },

    acceptDocument: (fileId: string, companyId: string) => {
        return client(`/v1/files/${fileId}/accept?company_id=${companyId}`, {
            method: 'POST',
        });
    },

    rejectDocument: (fileId: string, companyId: string, reason: string, customReason?: string) => {
        const params = new URLSearchParams();
        params.append('reason', reason);
        if (customReason) {
            params.append('custom_reason', customReason);
        }

        return client(`/v1/files/${fileId}/reject?company_id=${companyId}`, {
            method: 'POST',
            body: params.toString(),
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
        });
    },

    setDocumentExpiresAt: (fileId: string, expiresAt: string) => {
        const params = new URLSearchParams();
        params.append('expires_at', expiresAt);

        return client(`/v1/files/${fileId}/expires_at`, {
            method: 'PUT',
            body: params.toString(),
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
        });
    },

    fetchMissions: (params?: {
        statuses?: string[];
        no_candidate?: boolean;
        search?: string;
        sort?: AdminMissionSort;
        page?: number;
        page_size?: number;
    }) => {
        const qp = new URLSearchParams();
        if (params?.statuses?.length) qp.append('statuses', params.statuses.join(','));
        if (params?.no_candidate) qp.append('no_candidate', 'true');
        if (params?.search) qp.append('search', params.search);
        if (params?.sort) qp.append('sort', params.sort);
        if (params?.page !== undefined) qp.append('page', String(params.page));
        if (params?.page_size !== undefined) qp.append('page_size', String(params.page_size));
        const qs = qp.toString();
        const url = qs ? `/v1/admin/missions?${qs}` : '/v1/admin/missions';
        return client<ListAdminMissionsResponse>(url, { method: 'GET' });
    },

    // Invoice board (read-only)
    fetchInvoices: (params?: { status?: string; q?: string; limit?: number; offset?: number }) => {
        const queryParams = new URLSearchParams();
        if (params?.status) queryParams.append('status', params.status);
        if (params?.q) queryParams.append('q', params.q);
        if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString());
        if (params?.offset !== undefined) queryParams.append('offset', params.offset.toString());

        const queryString = queryParams.toString();
        const url = queryString ? `/v1/admin/invoices?${queryString}` : '/v1/admin/invoices';

        return client<ListAdminInvoicesResponse>(url, { method: 'GET' });
    },

    previewInvoice: (missionId: string, invoiceId: string) => {
        return client(`/v1/missions/${missionId}/invoices/${invoiceId}/preview`, {
            method: 'GET',
            responseType: 'blob',
        }) as Promise<Blob>;
    },

    // Finance KPIs (#177)
    fetchFinanceStats: (params?: { from?: string; to?: string }) => {
        const qp = new URLSearchParams();
        if (params?.from) qp.append('from', params.from);
        if (params?.to) qp.append('to', params.to);
        const qs = qp.toString();
        const url = qs ? `/v1/admin/stats/finance?${qs}` : '/v1/admin/stats/finance';
        return client<FinanceOverview>(url, { method: 'GET' });
    },

    exportFinance: (month: string) => {
        return client<Blob>(`/v1/admin/stats/finance/export?month=${encodeURIComponent(month)}`, {
            method: 'GET',
            responseType: 'blob',
        });
    },

    fetchFreelanceStats: (params?: { from?: string; to?: string }) => {
        const qp = new URLSearchParams();
        if (params?.from) qp.append('from', params.from);
        if (params?.to) qp.append('to', params.to);
        const qs = qp.toString();
        const url = qs ? `/v1/admin/stats/freelances?${qs}` : '/v1/admin/stats/freelances';
        return client<FreelanceOverview>(url, { method: 'GET' });
    },

    exportFreelances: (month: string) => {
        return client<Blob>(`/v1/admin/stats/freelances/export?month=${encodeURIComponent(month)}`, {
            method: 'GET',
            responseType: 'blob',
        });
    },

    fetchFreelanceBilling: (params?: { from?: string; to?: string; search?: string; sort?: string; collected_only?: boolean; page?: number; page_size?: number }) => {
        const qp = new URLSearchParams();
        if (params?.from) qp.append('from', params.from);
        if (params?.to) qp.append('to', params.to);
        if (params?.search) qp.append('search', params.search);
        if (params?.sort) qp.append('sort', params.sort);
        if (params?.collected_only) qp.append('collected_only', 'true');
        if (params?.page !== undefined) qp.append('page', String(params.page));
        if (params?.page_size !== undefined) qp.append('page_size', String(params.page_size));
        const qs = qp.toString();
        const url = qs ? `/v1/admin/stats/freelances/billing?${qs}` : '/v1/admin/stats/freelances/billing';
        return client<FreelanceBillingPage>(url, { method: 'GET' });
    },

    fetchCompanyStats: (params?: { from?: string; to?: string }) => {
        const qp = new URLSearchParams();
        if (params?.from) qp.append('from', params.from);
        if (params?.to) qp.append('to', params.to);
        const qs = qp.toString();
        const url = qs ? `/v1/admin/stats/companies?${qs}` : '/v1/admin/stats/companies';
        return client<CompanyOverview>(url, { method: 'GET' });
    },

    exportCompanies: (month: string) => {
        return client<Blob>(`/v1/admin/stats/companies/export?month=${encodeURIComponent(month)}`, {
            method: 'GET',
            responseType: 'blob',
        });
    },

    fetchCompanyMissions: (params?: { search?: string; sort?: string; ongoing_only?: boolean; page?: number; page_size?: number }) => {
        const qp = new URLSearchParams();
        if (params?.search) qp.append('search', params.search);
        if (params?.sort) qp.append('sort', params.sort);
        if (params?.ongoing_only) qp.append('ongoing_only', 'true');
        if (params?.page !== undefined) qp.append('page', String(params.page));
        if (params?.page_size !== undefined) qp.append('page_size', String(params.page_size));
        const qs = qp.toString();
        const url = qs ? `/v1/admin/stats/companies/missions?${qs}` : '/v1/admin/stats/companies/missions';
        return client<CompanyMissionsPage>(url, { method: 'GET' });
    },

    fetchMissionStats: (params?: { from?: string; to?: string }) => {
        const qp = new URLSearchParams();
        if (params?.from) qp.append('from', params.from);
        if (params?.to) qp.append('to', params.to);
        const qs = qp.toString();
        const url = qs ? `/v1/admin/stats/missions?${qs}` : '/v1/admin/stats/missions';
        return client<MissionStats>(url, { method: 'GET' });
    },

    exportMissions: (month: string) => {
        return client<Blob>(`/v1/admin/stats/missions/export?month=${encodeURIComponent(month)}`, {
            method: 'GET',
            responseType: 'blob',
        });
    },

    fetchMissionDetail: (params?: { search?: string; statuses?: string; sort?: string; page?: number; page_size?: number; from?: string; to?: string }) => {
        const qp = new URLSearchParams();
        if (params?.search) qp.append('search', params.search);
        if (params?.statuses) qp.append('statuses', params.statuses);
        if (params?.sort) qp.append('sort', params.sort);
        if (params?.page !== undefined) qp.append('page', String(params.page));
        if (params?.page_size !== undefined) qp.append('page_size', String(params.page_size));
        if (params?.from) qp.append('from', params.from);
        if (params?.to) qp.append('to', params.to);
        const qs = qp.toString();
        const url = qs ? `/v1/admin/stats/missions/detail?${qs}` : '/v1/admin/stats/missions/detail';
        return client<MissionDetailPage>(url, { method: 'GET' });
    },

    // Referral validation (#191)
    listReferrals: (status: string = 'accepted') => {
        return client<AdminReferralInvitation[]>(`/v1/admin/referrals?status=${encodeURIComponent(status)}`, {
            method: 'GET',
        });
    },

    validateReferral: (invitationId: string) => {
        return client(`/v1/admin/referrals/${invitationId}/validate`, {
            method: 'POST',
        });
    },

    rejectReferral: (invitationId: string) => {
        return client(`/v1/admin/referrals/${invitationId}/reject`, {
            method: 'POST',
        });
    },

    fetchAdminCompanies: (params?: { search?: string; page?: number; page_size?: number }) => {
        const qp = new URLSearchParams();
        if (params?.search) qp.append('search', params.search);
        if (params?.page !== undefined) qp.append('page', String(params.page));
        if (params?.page_size !== undefined) qp.append('page_size', String(params.page_size));
        const qs = qp.toString();
        return client<AdminCompaniesPage>(qs ? `/v1/admin/companies?${qs}` : '/v1/admin/companies', { method: 'GET' });
    },

    fetchAdminCompany: (companyId: string, options: { skipGlobalErrorAlert?: boolean } = {}) => {
        return client<AdminCompanyDetail>(`/v1/admin/companies/${companyId}`, { method: 'GET', ...options });
    },

    // The modal shows its own translated message, never the raw backend key.
    setCompanyContactEmail: (companyId: string, contactEmail: string, options: { skipGlobalErrorAlert?: boolean } = {}) => {
        return client(`/v1/admin/companies/${companyId}/contact-email`, {
            method: 'PUT',
            body: { contact_email: contactEmail },
            ...options,
        });
    },
});
